import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useUserInfo from '@/hooks/users/use-user-info';
import { type RespondedType, ResponseError } from '@/models/response';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { allChatSessionQueryOptions } from '../sessions/use-conversation-sessions';
import { generateFriendInfoQueryOptions } from '../users/use-friend-info';
import { friendsQueryOptions } from './use-friends';

interface UseApplyFriendParams {
  userId: number | undefined;
  qrCode?: string | undefined;
  vChatId?: string | undefined;
  groupId?: number | undefined;
  sessionId?: number | undefined;
  source?: 'contact' | 'search' | 'avatar' | 'qrcode';
}

export default function useApplyFriend({
  userId,
  qrCode,
  vChatId,
  groupId,
  sessionId,
  source = 'avatar',
}: UseApplyFriendParams) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { checkCanAddFriend } = useGroupInfo(groupId);
  const { data: userInfo } = useUserInfo();

  const canAddFriend = checkCanAddFriend(userId ?? 0);

  const mutation = useMutation<RespondedType, ResponseError, string>({
    mutationKey: ['/ApplyFriend'],
    mutationFn: async (params) => {
      const requestSource = getRequestSource({ groupId, sessionId, source });

      let response: RespondedType;

      if (source === 'qrcode' && qrCode) {
        response = await fetcher<RespondedType>(
          '/ApplyFriendQRCode',
          {
            Remark: params || t('user.remark.placeholder', { name: userInfo?.NickName }),
            QRCode: decodeURIComponent(qrCode),
            Source: requestSource,
          },
          { validResponseCodes: [0] },
        );
      } else {
        response = await fetcher<RespondedType>(
          '/ApplyFriend',
          {
            Remark: params || t('user.remark.placeholder', { name: userInfo?.NickName }),
            UserID: userId,
            GroupID: groupId,
            VChatID: vChatId,
            Source: requestSource,
          },
          { validResponseCodes: [0] },
        );
      }

      return response;
    },
    onSuccess: async () => {
      await Promise.allSettled([
        queryClient.invalidateQueries(friendsQueryOptions),
        queryClient.invalidateQueries(generateFriendInfoQueryOptions(userId)),
        queryClient.invalidateQueries(allChatSessionQueryOptions),
      ]);

      addToast({
        type: 'success',
        message: t('common.submit-success'),
      });
    },
    onError: (error) => {
      if (ResponseError.isResponseError(error)) {
        addToast({
          type: 'error',
          message: error.message,
        });
      } else {
        addToast({
          type: 'error',
          message: t('common.submit-failed'),
        });
        console.error(error);
      }
    },
  });

  return {
    applyFriend: mutation.mutateAsync,
    canAddFriend,
    ...mutation,
  };
}

function getRequestSource({
  groupId,
  sessionId,
  source = 'avatar',
}: Pick<UseApplyFriendParams, 'groupId' | 'sessionId' | 'source'>) {
  switch (source) {
    case 'contact':
      return groupId ? `Group:${groupId}:Card` : `Session:${sessionId}:Card`;
    case 'search':
      return 'Search';
    case 'avatar':
      return groupId ? `Group:${groupId}:Head` : `Session:${sessionId}:Card`;
    case 'qrcode':
      return 'QRCode';
  }
}
